import {useEffect, useLayoutEffect, useState} from 'react'
import {StyleSheet, TouchableWithoutFeedback, View} from 'react-native'
import {useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'
import {RemoveScrollBar} from 'react-remove-scroll-bar'

import {useIntentHandler} from '#/lib/hooks/useIntentHandler'
import {useWebMediaQueries} from '#/lib/hooks/useWebMediaQueries'
import {type NavigationProp} from '#/lib/routes/types'
import {useLockScreenByAge} from '#/lib/wsoc/lockScreenByAge/LockScreenByAge.ts'
import * as persisted from '#/state/persisted'
import {useReconcileDeadFeeds} from '#/state/queries/preferences'
import {useSession} from '#/state/session'
import {useIsDrawerOpen, useSetDrawerOpen} from '#/state/shell'
import {useComposerKeyboardShortcut} from '#/state/shell/composer/useComposerKeyboardShortcut'
import {useCloseAllActiveElements} from '#/state/util'
import {ErrorBoundary} from '#/view/com/util/ErrorBoundary'
import {Deactivated} from '#/screens/Deactivated'
import {Takendown} from '#/screens/Takendown'
import {WidLockScreenByAge} from '#/screens/WidLockScreenByAge'
import {WIDUpgradeModal} from '#/screens/WIDUpgrade'
import {atoms as a, select, useTheme} from '#/alf'
import {AgeAssuranceRedirectDialog} from '#/components/ageAssurance/AgeAssuranceRedirectDialog'
import {
  useWIDLockByAgeUpgradeControl,
  useWIDUpgradeControl,
} from '#/components/dialogs/Context'
import {EmailDialog} from '#/components/dialogs/EmailDialog'
import {LinkWarningDialog} from '#/components/dialogs/LinkWarning'
import {MutedWordsDialog} from '#/components/dialogs/MutedWords'
// import {NuxDialogs} from '#/components/dialogs/nuxs'
import {SigninDialog} from '#/components/dialogs/Signin'
import {ExternalEntrySignupBanner} from '#/components/ExternalEntrySignupBanner.web'
import {Lightbox} from '#/components/Lightbox'
import {
  Outlet as PolicyUpdateOverlayPortalOutlet,
  usePolicyUpdateContext,
} from '#/components/PolicyUpdateOverlay'
import {Outlet as PortalOutlet} from '#/components/Portal'
import {useAgeAssurance} from '#/ageAssurance'
// import {NoAccessScreen} from '#/ageAssurance/components/NoAccessScreen'
import {RedirectOverlay} from '#/ageAssurance/components/RedirectOverlay'
import {AnnouncementDialogs} from '#/features/announcements'
import {FlatNavigator, RoutesContainer} from '#/Navigation'
import {useSemanticTokens} from '#/wsocial/service/design-system'
import {Composer} from './Composer.web'
import {DrawerContent} from './Drawer'

function ShellInner() {
  const t = useTheme()
  const isDrawerOpen = useIsDrawerOpen()
  const setDrawerOpen = useSetDrawerOpen()
  const {isDesktop} = useWebMediaQueries()
  const navigator = useNavigation<NavigationProp>()
  const closeAllActiveElements = useCloseAllActiveElements()
  const {t: l} = useLingui()
  const showDrawer = !isDesktop && isDrawerOpen
  const [showDrawerDelayedExit, setShowDrawerDelayedExit] = useState(showDrawer)
  const {state: policyUpdateState} = usePolicyUpdateContext()
  const widUpgradeControl = useWIDUpgradeControl()
  const LockByAgeUpgradeControl = useWIDLockByAgeUpgradeControl()
  // const welcomeModalControl = useWelcomeModal()

  useLockScreenByAge()

  useLayoutEffect(() => {
    if (showDrawer !== showDrawerDelayedExit) {
      if (showDrawer) {
        setShowDrawerDelayedExit(true)
      } else {
        const timeout = setTimeout(() => {
          setShowDrawerDelayedExit(false)
        }, 160)
        return () => clearTimeout(timeout)
      }
    }
  }, [showDrawer, showDrawerDelayedExit])

  useComposerKeyboardShortcut()
  useIntentHandler()

  useEffect(() => {
    const unsubscribe = navigator.addListener('state', () => {
      closeAllActiveElements()
    })
    return unsubscribe
  }, [navigator, closeAllActiveElements])

  return (
    <>
      <ErrorBoundary>
        <FlatNavigator />
      </ErrorBoundary>
      <Composer />
      <MutedWordsDialog />
      <SigninDialog />
      <EmailDialog />
      <AgeAssuranceRedirectDialog />
      <LinkWarningDialog />
      <Lightbox />
      {/* <NuxDialogs /> */}
      <AnnouncementDialogs />
      <ExternalEntrySignupBanner />
      {widUpgradeControl.isOpen && (
        <WIDUpgradeModal onClose={widUpgradeControl.close} />
      )}
      {LockByAgeUpgradeControl.isOpen && (
        <WidLockScreenByAge onClose={LockByAgeUpgradeControl.close} />
      )}

      {/*{welcomeModalControl.isOpen && (
        <WelcomeModal control={welcomeModalControl} />
      )}*/}

      {/* Until policy update has been completed by the user, don't render anything that is portaled */}
      {policyUpdateState.completed && (
        <>
          <PortalOutlet />
        </>
      )}

      {showDrawerDelayedExit && (
        <>
          <RemoveScrollBar />
          <TouchableWithoutFeedback
            onPress={ev => {
              // Only close if press happens outside of the drawer
              if (ev.target === ev.currentTarget) {
                setDrawerOpen(false)
              }
            }}
            accessibilityLabel={l`Close drawer menu`}
            accessibilityHint="">
            <View
              style={[
                styles.drawerMask,
                {
                  backgroundColor: showDrawer
                    ? select(t.name, {
                        light: 'rgba(0, 57, 117, 0.1)',
                        dark: 'rgba(1, 82, 168, 0.1)',
                        dim: 'rgba(10, 13, 16, 0.8)',
                      })
                    : 'transparent',
                },
                a.transition_color,
              ]}>
              <View
                style={[
                  styles.drawerContainer,
                  showDrawer ? a.slide_in_left : a.slide_out_left,
                ]}>
                <DrawerContent />
              </View>
            </View>
          </TouchableWithoutFeedback>
        </>
      )}

      <PolicyUpdateOverlayPortalOutlet />
    </>
  )
}

export function Shell() {
  const s = useSemanticTokens()
  // const aa = useAgeAssurance()
  const {currentAccount} = useSession()

  // Once-per-session GC for saved feeds whose underlying
  // app.bsky.feed.generator record was removed (toggle off on the
  // feed-generator's /admin). The hook is safe to call at the top level
  // — early-returns when there's no session or no feeds. See
  // useReconcileDeadFeeds docstring for safety guarantees.
  useReconcileDeadFeeds()
  return (
    <View
      testID="webShellView"
      style={[a.util_screen_outer, {backgroundColor: s.bg.default}]}>
      {currentAccount?.status === 'takendown' ? (
        <Takendown />
      ) : currentAccount?.status === 'deactivated' ? (
        <Deactivated />
      ) : (
        <>
          {/*{aa.state.access === aa.Access.None ? (
            <NoAccessScreen />
          ) : (*/}
          <RoutesContainer>
            <ShellInner />
          </RoutesContainer>
          {/* )}*/}

          <RedirectOverlay />
        </>
      )}
    </View>
  )
}

const styles = StyleSheet.create({
  drawerMask: {
    ...a.fixed,
    width: '100%',
    height: '100%',
    top: 0,
    left: 0,
  },
  drawerContainer: {
    display: 'flex',
    ...a.fixed,
    top: 0,
    left: 0,
    height: '100%',
    width: 330,
    maxWidth: '80%',
  },
})
